/**
 * Escrow account calculations for 1031 exchange QI-held funds.
 * Computes interest accrual, QI fees, and net available balance.
 */

export interface EscrowData {
  depositDate: string           // ISO date
  principalAmount: number       // From relinquished property sale proceeds
  annualRatePercent: number     // Default 4.5% APY
  qiFeeFlat: number             // e.g. $750
  qiFeePercent: number          // e.g. 0.1 (meaning 0.1%)
  disbursements: Array<{ date: string; amount: number; description: string }>
  status: 'deposited' | 'pending_disbursement' | 'partially_disbursed' | 'disbursed'
}

export interface EscrowSummary {
  principalAmount: number
  daysInEscrow: number
  interestAccrued: number
  dailyInterest: number
  qiFees: number
  totalDisbursed: number
  currentBalance: number
  netAvailable: number
  status: EscrowData['status']
  balanceHistory: Array<{ day: number; date: string; balance: number; projected: boolean }>
}

export function calculateDaysInEscrow(depositDate: string): number {
  const deposit = new Date(depositDate)
  const now = new Date()
  const diffMs = now.getTime() - deposit.getTime()
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)))
}

export function calculateInterestAccrued(
  principal: number,
  annualRatePercent: number,
  days: number
): number {
  if (principal <= 0 || annualRatePercent <= 0 || days <= 0) return 0
  // Daily compounding: A = P(1 + r/365)^days - P
  const dailyRate = annualRatePercent / 100 / 365
  return principal * (Math.pow(1 + dailyRate, days) - 1)
}

export function calculateDailyInterest(
  principal: number,
  annualRatePercent: number
): number {
  if (principal <= 0 || annualRatePercent <= 0) return 0
  return principal * (annualRatePercent / 100 / 365)
}

export function calculateQIFees(
  principal: number,
  feeFlat: number,
  feePercent: number
): number {
  return feeFlat + (principal * feePercent / 100)
}

export function calculateEscrowSummary(data: EscrowData): EscrowSummary {
  const safeData = {
    depositDate: data.depositDate ?? (data as { fundedAt?: string }).fundedAt ?? new Date().toISOString(),
    principalAmount: typeof data.principalAmount === 'number' ? data.principalAmount : 0,
    annualRatePercent: typeof data.annualRatePercent === 'number' ? data.annualRatePercent : 3,
    qiFeeFlat: typeof data.qiFeeFlat === 'number' ? data.qiFeeFlat : 750,
    qiFeePercent: typeof data.qiFeePercent === 'number' ? data.qiFeePercent : 0.1,
    disbursements: Array.isArray(data.disbursements) ? data.disbursements : [],
    status: data.status ?? 'deposited',
  }

  const daysInEscrow = calculateDaysInEscrow(safeData.depositDate)
  const interestAccrued = calculateInterestAccrued(
    safeData.principalAmount,
    safeData.annualRatePercent,
    daysInEscrow
  )
  const dailyInterest = calculateDailyInterest(safeData.principalAmount, safeData.annualRatePercent)
  const qiFees = calculateQIFees(safeData.principalAmount, safeData.qiFeeFlat, safeData.qiFeePercent)
  const totalDisbursed = safeData.disbursements.reduce((sum, d) => sum + d.amount, 0)
  const currentBalance = safeData.principalAmount + interestAccrued
  const netAvailable = currentBalance - qiFees - totalDisbursed

  const holdHorizonDays = 180
  const historyEndDay = Math.max(holdHorizonDays, daysInEscrow)
  const balanceHistory: EscrowSummary['balanceHistory'] = []
  const depositDateObj = new Date(safeData.depositDate)

  for (let d = 0; d <= historyEndDay; d++) {
    const dayDate = new Date(depositDateObj)
    dayDate.setDate(dayDate.getDate() + d)
    const dayInterest = calculateInterestAccrued(safeData.principalAmount, safeData.annualRatePercent, d)
    balanceHistory.push({
      day: d,
      date: dayDate.toISOString().split('T')[0],
      balance: safeData.principalAmount + dayInterest,
      projected: d > daysInEscrow,
    })
  }

  return {
    principalAmount: safeData.principalAmount,
    daysInEscrow,
    interestAccrued,
    dailyInterest,
    qiFees,
    totalDisbursed,
    currentBalance,
    netAvailable,
    status: safeData.status,
    balanceHistory,
  }
}

/**
 * Create default escrow data from a selling price.
 * Called when Sales Hub is submitted.
 */
export function createEscrowData(
  sellingPrice: number,
  sellingExpenses: number = 0
): EscrowData {
  const netProceeds = Math.max(0, sellingPrice - sellingExpenses)
  return {
    depositDate: new Date().toISOString(),
    principalAmount: netProceeds,
    annualRatePercent: 3,
    qiFeeFlat: 750,
    qiFeePercent: 0.1,
    disbursements: [],
    status: 'deposited',
  }
}
